import Button from "@amzn/meridian/button";
import Divider from "@amzn/meridian/divider";
import Message from "@amzn/meridian/message";
import CsvTable from "src/components/shared/csvTable/csvTable";
import { formatISO } from "date-fns";
import { useMemo } from "react";
import Plot from "react-plotly.js";
import ResponseConfirmation from "src/components/pages/home/components/chat/chatResult/responseConfirmation";
import BotMessage from "src/components/shared/botMessage/botMessage";
import EmptyChartMessage from "src/components/shared/emptyChartMessage/EmptyChartMessage";
import DenyConfirmationMessage from "src/components/shared/denyConfirmationMessage/denyConfirmationMessage";
import { BADGE_PHOTO_THUMBNAIL_URL } from "src/config/external-links";
import { DEFAULT_PLOTLY_CONFIG } from "src/constants/constants";
import chatIcon from "src/images/safer_chat_icon.png";
import { useAppSelector } from "src/store/store";
import { ChatResponse } from "../../../store/types";
import Vote from "../vote/vote";
import "./chatResult.scss";
import CitationText from "src/components/shared/citationText/citationText";
import { processCitations } from "src/utils/citationUtils";
import SourcesDropdown from "src/components/shared/sourcesDropDown/sourcesDropdown";

interface ChatResultProps {
  output: ChatResponse;
  isOnMcpRoute: boolean;
}
const ChatResult = ({ output, isOnMcpRoute }: ChatResultProps) => {
  const { alias } = useAppSelector((state) => state.user);
  const { streaming } = useAppSelector((state) => state.chatSlice);
  const botName = !isOnMcpRoute ? "Saige" : "Safer";
  const chartJson: any = useMemo(() => {
    if (output?.chartResult?.length) {
      return JSON.parse(output.chartResult);
    }
  }, [output.chartResult]);

  // Don't render if this is a streaming message that's currently being processed
  if (
    isOnMcpRoute &&
    streaming.isStreaming &&
    output.messageId === streaming.messageId
  ) {
    return null;
  }

  return (
    <div>
      {output.isTextOnly ? (
        <div className="user-bot-response">
          <Message
            name={botName}
            datetime={output.datetime || formatISO(new Date())}
            avatarImageSource={chatIcon}
            type="outgoing"
            maxWidth="750px"
          >
            <CitationText
              text={processCitations(
                output.llmMessage,
                output.sourceAttributions,
              )}
              citations={output.sourceAttributions}
            />
            {output.relatedDocuments && (
              <>
                <Divider spacingBefore="400" spacingAfter="400" />
                {/* shows the document links in drop-down menu */}
                <SourcesDropdown relatedDocuments={output.relatedDocuments} />
              </>
            )}
            {/* Adding Vote component to get feedback from users */}
            <Vote
              messageId={output.messageId}
              llmResponse={output.llmResponse}
              voteResponse={output.voteResponse}
              thumbsUpClicked={output.thumbsUpClicked}
              thumbsDownClicked={output.thumbsDownClicked}
            />
          </Message>
        </div>
      ) : (
        <div className="chat-result-container">
          <div className="result-confirmation-message user-bot-response">
            <Message
              name={botName}
              datetime={output.datetime || formatISO(new Date())}
              avatarImageSource={chatIcon}
              type="outgoing"
              maxWidth="750px"
            >
              {output.llmMessage}
              <ResponseConfirmation output={output} />
            </Message>
          </div>
          {output.confirmationYesClicked && (
            <>
              <div className="user-bot-response">
                <Message
                  name={`${alias}`}
                  datetime={output.datetime || formatISO(new Date())}
                  avatarImageSource={`${BADGE_PHOTO_THUMBNAIL_URL}${alias}`}
                  type="incoming"
                  maxWidth="750px"
                >
                  <Button disabled={true}>Yes</Button>
                </Message>
              </div>
              <BotMessage datetime={output.datetime || formatISO(new Date())}>
                {output.isEmptyData ? (
                  <EmptyChartMessage containerWidth="660px" />
                ) : (
                  <>
                    {output.numericalAnswer && (
                      <div style={{ width: "660px", marginBottom: "20px" }}>
                        {output.numericalAnswer}
                      </div>
                    )}

                    {output.showCsv && output.csvPreview ? (
                      <CsvTable
                        csvPreview={output.csvPreview}
                        csvData={output.csvData}
                      />
                    ) : (
                      output.chartResult &&
                      chartJson && (
                        <Plot
                          style={{ width: "700px", height: "450px" }}
                          data={chartJson.data}
                          layout={chartJson.layout}
                          config={DEFAULT_PLOTLY_CONFIG}
                        />
                      )
                    )}
                  </>
                )}
                <Vote
                  messageId={output.messageId}
                  llmResponse={output.llmResponse}
                  voteResponse={output.voteResponse}
                  thumbsUpClicked={output.thumbsUpClicked}
                  thumbsDownClicked={output.thumbsDownClicked}
                />
              </BotMessage>
            </>
          )}
          {output.confirmationNoClicked && (
            <>
              <Message
                name={`${alias}`}
                datetime={output.datetime || formatISO(new Date())}
                avatarImageSource={`${BADGE_PHOTO_THUMBNAIL_URL}${alias}`}
                type="incoming"
                maxWidth="750px"
              >
                <Button
                  disabled={true}
                  className="confirmation-button-clicked-no"
                >
                  No
                </Button>
              </Message>
              <DenyConfirmationMessage />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatResult;
